/**
 * escapeShellDollars
 *
 * 把"看起来不是 LaTeX 公式分隔符"的 $ 字符做安全处理，避免 KaTeX /
 * markdown-it-katex 把诸如 `echo $PATH`、`$(date)`、`${VAR}`、`价格 $100`
 * 之类内容当成行内公式吃掉。
 *
 * 两种 mode：
 *   - mode='markdown'（默认，给 markdown-it 用）
 *       输出 `\$`。markdown-it 的 escape 规则在 markdown-it-katex 的 math_inline
 *       之前运行，会把 `\$` 消解为字面 `$`，最终 HTML 没有任何额外字符。
 *
 *   - mode='html'（给 v-latex 这种"先 innerHTML 再让 KaTeX auto-render 扫描"的
 *     场景用）
 *       输出 `<span class="vlsd">$</span>`。KaTeX 的 renderElem 只合并相邻
 *       的 *text* 兄弟节点（见 katex/contrib/auto-render/auto-render.ts），
 *       中间一插入 element 就会切断 text node 串，KaTeX 的 splitAtDelimiters
 *       从而无法把这个 $ 与其它 $ 配成对。浏览器照常显示 $ 字符，没有视觉副作用。
 *       注意：v-latex 指令需要把 'vlsd' 加入 ignoredClasses 做防御性兜底。
 *       直接写 `\$` 不行，因为 innerHTML 不解析 \ 转义、且 KaTeX 在寻找
 *       *开启* 分隔符时不检查前导 \，反而会让用户看到字面 "\$"。
 *
 * 设计原则（保守，倾向"不渲染"）：
 *  1. 代码区（<pre>/<code>/反引号/围栏代码块）以及 HTML 标签内部的 $ 一律不动；
 *  2. 已经写成 \$ 的转义 $ 一律不动；
 *  3. 双美元 $$...$$ 优先按块级公式判定：能配对、且内部含 LaTeX 特征字符才保留；
 *  4. 单美元 $...$ 优先按行内公式判定：能配对、且内部含 LaTeX 特征字符才保留;
 *  5. 否则——若紧跟 Shell 触发字符（[A-Za-z_({0-9?#@*$!-]）→ 转义当前 $；
 *  6. 否则——若已配对但内部无特征 → 把这对 $ 一并转义；
 *  7. 否则——落单的 $ → 转义。
 *
 * "LaTeX 特征字符"指 \、^、_、{、} 或常见 macro（\frac、\sqrt、希腊字母等）。
 * 真公式几乎必带这些字符；不含特征的成对 $...$ 在历史题库里多半是
 * "价格 $100, 优惠 $200" 之类的误用，渲染时跳过比错渲染更安全。
 */

export type EscapeMode = 'markdown' | 'html'

const LATEX_FEATURE_RE = /[\\^_{}]|\\(?:frac|sqrt|sum|int|prod|lim|cdot|times|div|pm|mp|approx|leq|geq|neq|infty|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|vartheta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega|hat|bar|tilde|vec|dot|ddot|left|right|begin|end|mathbf|mathrm|mathcal|mathbb|text|operatorname|displaystyle|partial|nabla|forall|exists|in|notin|subset|supset|cup|cap)\b/

const SHELL_TRIGGER_RE = /[A-Za-z_({0-9?#@*$!\-]/

/** v-latex 指令应把这个 class 加入 KaTeX 的 ignoredClasses 列表 */
export const SKIP_DOLLAR_CLASS = 'vlsd'

const HTML_DOLLAR_OPEN = `<span class="${SKIP_DOLLAR_CLASS}">`
const HTML_DOLLAR_CLOSE = '</span>'

const PLACEHOLDER_PREFIX = '\u0000ESDOL'
const PLACEHOLDER_SUFFIX = '\u0000'
const PLACEHOLDER_RE = /\u0000ESDOL(\d+)\u0000/g

function escapedDollar(mode: EscapeMode): string {
  return mode === 'html' ? `${HTML_DOLLAR_OPEN}$${HTML_DOLLAR_CLOSE}` : '\\$'
}

function isEscapedDollar(text: string, idx: number): boolean {
  let count = 0
  for (let p = idx - 1; p >= 0 && text[p] === '\\'; p--) count++
  return count % 2 === 1
}

function findInlinePair(text: string, from: number): number {
  for (let k = from; k < text.length; k++) {
    const c = text[k]
    if (c === '\n') return -1
    if (c === '$') {
      if (isEscapedDollar(text, k)) continue
      // 紧贴另一个 $ 视为块级，不在行内配对
      if (text[k + 1] === '$') return -1
      return k
    }
  }
  return -1
}

function findBlockClosing(text: string, from: number): number {
  for (let k = from; k <= text.length - 2; k++) {
    if (text[k] === '$' && text[k + 1] === '$') {
      if (!isEscapedDollar(text, k)) return k
    }
  }
  return -1
}

function maskCodeRegions(input: string): { masked: string; masks: string[] } {
  const masks: string[] = []
  const place = (m: string) => {
    const i = masks.length
    masks.push(m)
    return `${PLACEHOLDER_PREFIX}${i}${PLACEHOLDER_SUFFIX}`
  }

  let masked = input
  // 顺序敏感：先吃围栏/块级，再吃行内/标签
  masked = masked.replace(/```[\s\S]*?```/g, place)
  masked = masked.replace(/<pre\b[^>]*>[\s\S]*?<\/pre>/gi, place)
  masked = masked.replace(/<code\b[^>]*>[\s\S]*?<\/code>/gi, place)
  masked = masked.replace(/`[^`\n]+`/g, place)
  // 屏蔽剩余 HTML 标签本身（避免 <a href="?p=$1"> 这类 attribute 被改写）
  masked = masked.replace(/<[^<>]+>/g, place)

  return { masked, masks }
}

function unmask(text: string, masks: string[]): string {
  return text.replace(PLACEHOLDER_RE, (full, idx) => {
    const i = Number(idx)
    return Number.isFinite(i) && i < masks.length ? masks[i] : full
  })
}

export function escapeNonMathDollars(input: string, mode: EscapeMode = 'markdown'): string {
  if (!input) return ''
  if (input.indexOf('$') === -1) return input

  const ESC = escapedDollar(mode)

  const { masked, masks } = maskCodeRegions(input)
  const out: string[] = []
  let i = 0
  const len = masked.length

  while (i < len) {
    const ch = masked[i]
    if (ch !== '$') {
      out.push(ch)
      i++
      continue
    }

    if (isEscapedDollar(masked, i)) {
      out.push(ch)
      i++
      continue
    }

    // $$ 块级
    if (masked[i + 1] === '$') {
      const close = findBlockClosing(masked, i + 2)
      if (close === -1) {
        out.push(ESC, ESC)
        i += 2
        continue
      }
      const inner = masked.slice(i + 2, close)
      if (LATEX_FEATURE_RE.test(inner)) {
        out.push('$$', inner, '$$')
      } else {
        out.push(ESC, ESC, inner, ESC, ESC)
      }
      i = close + 2
      continue
    }

    // 单 $：先尝试行内配对
    const close = findInlinePair(masked, i + 1)
    if (close !== -1) {
      const inner = masked.slice(i + 1, close)
      if (LATEX_FEATURE_RE.test(inner)) {
        out.push('$', inner, '$')
        i = close + 1
        continue
      }
      // 配对成功但内部无 LaTeX 特征
      const next = masked[i + 1]
      if (next && SHELL_TRIGGER_RE.test(next)) {
        // 形如 $X ... $Y：开头是 Shell 模式，只转义当前 $，留给后续 $ 自己判定
        out.push(ESC)
        i++
        continue
      }
      // 普通非公式配对（如 "价格 $100, 优惠 $200" 的某种形态）→ 一并转义
      out.push(ESC, inner, ESC)
      i = close + 1
      continue
    }

    // 落单的 $
    out.push(ESC)
    i++
  }

  return unmask(out.join(''), masks)
}

export default escapeNonMathDollars
